import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { useAuth } from "@/contexts/AuthContext";

export interface EvolucaoClinica {
  id: string;
  paciente_id: string;
  dentista_id?: string;
  data: string;
  procedimento: string;
  evolucao: string;
  conduta?: string;
  retorno?: string;
  anexos_texto?: string;
  created_at?: string;
  updated_at?: string;
}

export type EvolucaoClinicaInput = Omit<EvolucaoClinica, "id" | "created_at" | "updated_at">;

export function useEvolucoesClinicas(pacienteId?: string) {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [evolucoes, setEvolucoes] = useState<EvolucaoClinica[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEvolucoes = async () => {
    if (!user || !pacienteId) {
      setEvolucoes([]);
      return;
    }

    try {
      setLoading(true);
      const data = await apiList<EvolucaoClinica>("evolucoes_clinicas");
      setEvolucoes(
        data
          .filter((item) => item.paciente_id === pacienteId)
          .sort((a, b) => String(b.data || b.created_at || "").localeCompare(String(a.data || a.created_at || "")))
      );
    } catch (error: any) {
      toast.error("Erro ao carregar evolução clínica: " + error.message);
      setEvolucoes([]);
    } finally {
      setLoading(false);
    }
  };

  const createEvolucao = async (input: EvolucaoClinicaInput) => {
    try {
      const data = await apiCreate<EvolucaoClinica>("evolucoes_clinicas", input);
      await fetchEvolucoes();
      toast.success("Evolução clínica registrada!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao registrar evolução: " + error.message);
      return null;
    }
  };

  const updateEvolucao = async (id: string, input: Partial<EvolucaoClinicaInput>) => {
    try {
      const data = await apiUpdate<EvolucaoClinica>("evolucoes_clinicas", id, input);
      await fetchEvolucoes();
      toast.success("Evolução atualizada!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao atualizar evolução: " + error.message);
      return null;
    }
  };

  const deleteEvolucao = async (id: string) => {
    try {
      await apiDelete("evolucoes_clinicas", id);
      await fetchEvolucoes();
      toast.success("Evolução removida!");
    } catch (error: any) {
      toast.error("Erro ao remover evolução: " + error.message);
    }
  };

  useEffect(() => {
    fetchEvolucoes();
  }, [user?.id, activeClinicaId, pacienteId]);

  return { evolucoes, loading, fetchEvolucoes, createEvolucao, updateEvolucao, deleteEvolucao };
}
